/**
 * Centralni logging utilities modul.
 *
 * Svi logging komponenti (client i server) trebaju koristiti ove funkcije
 * za konzistentnu obradu i serijalizaciju error-a.
 */

// ---------------------------------------------------------------------------
// Error to Object Conversion
// ---------------------------------------------------------------------------

/**
 * Sigurno serijalizira Error objekt u plain object koji može biti JSON.stringify-an.
 */
function errorToObject(err: Error): Record<string, unknown> {
  const obj: Record<string, unknown> = {
    name: err.name,
    message: err.message,
  };

  if (err.stack) {
    obj.stack = err.stack;
  }

  // Uključi sve custom propertije (code, cause, itd.)
  for (const key of Object.getOwnPropertyNames(err)) {
    if (!(key in obj)) {
      obj[key] = (err as unknown as Record<string, unknown>)[key];
    }
  }

  // Rekurzivno obradi 'cause' ako postoji
  if ('cause' in err && err.cause instanceof Error) {
    obj.cause = errorToObject(err.cause);
  }

  return obj;
}

// ---------------------------------------------------------------------------
// Safe JSON Stringify
// ---------------------------------------------------------------------------

/**
 * Sigurno serijalizira bilo koju vrijednost u JSON string.
 * Rješava:
 *  - Error objekte (ekstrahira name, message, stack, cause)
 *  - Circular reference (zamjenjuje sa "[Circular]")
 *  - BigInt (konvertuje u string)
 *  - Neserijalizabilne objekte (fallback na opis)
 */
export function safeStringify(value: unknown, indent?: number): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'bigint') return value.toString();

  // Error objekti - posebna obrada
  if (value instanceof Error) {
    try {
      return JSON.stringify(errorToObject(value), null, indent);
    } catch {
      return `${value.name}: ${value.message}\n${value.stack ?? ''}`;
    }
  }

  // Objekti i nizovi
  try {
    const seen = new WeakSet();
    return JSON.stringify(
      value,
      (_key, val: unknown) => {
        // BigInt podrška
        if (typeof val === 'bigint') {
          return val.toString();
        }
        // Error unutar objekta
        if (val instanceof Error) {
          return errorToObject(val);
        }
        // Circular reference detekcija
        if (typeof val === 'object' && val !== null) {
          if (seen.has(val)) {
            return '[Circular]';
          }
          seen.add(val);
        }
        return val;
      },
      indent
    );
  } catch {
    // Ako i ovo padne, pokušaj osnovni opis
    try {
      if (typeof value === 'object' && value !== null) {
        const keys = Object.keys(value as object);
        return `{Object with keys: ${keys.slice(0, 10).join(', ')}${keys.length > 10 ? '...' : ''}}`;
      }
      return Object.prototype.toString.call(value);
    } catch {
      return '[Unserializable]';
    }
  }
}

// ---------------------------------------------------------------------------
// Error Formatting
// ---------------------------------------------------------------------------

/**
 * Formatira Error ili error-like objekt u čitljiv string za prikaz.
 */
export function formatErrorForLog(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;

  // Error objekt
  if (value instanceof Error) {
    let result = `${value.name}: ${value.message}`;
    if (value.stack) {
      result += `\n${value.stack}`;
    }
    if ('cause' in value && value.cause) {
      result += `\nCaused by: ${formatErrorForLog(value.cause)}`;
    }
    return result;
  }

  // Error-like objekt (ima message property)
  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, unknown>;
    if ('message' in obj || 'error' in obj || 'err' in obj) {
      const parts: string[] = [];
      if (obj.name) parts.push(`[${String(obj.name)}]`);
      if (obj.message) parts.push(String(obj.message));
      if (obj.error) parts.push(`Error: ${safeStringify(obj.error)}`);
      if (obj.err) parts.push(`Err: ${safeStringify(obj.err)}`);
      if (obj.stack) parts.push(`Stack: ${String(obj.stack)}`);
      if (obj.code) parts.push(`Code: ${String(obj.code)}`);
      if (parts.length > 0) {
        return parts.join(' | ');
      }
    }
  }

  // Fallback na JSON
  return safeStringify(value);
}

// ---------------------------------------------------------------------------
// String Utilities
// ---------------------------------------------------------------------------

/**
 * Skraćuje string na maksimalnu dužinu, dodajući marker ako je skraćen.
 */
export function truncateString(value: string, max = 100_000): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max)}… [truncated, total: ${value.length}]`;
}

// ---------------------------------------------------------------------------
// Log Payload Preparation
// ---------------------------------------------------------------------------

export type LogLevel = 'info' | 'warn' | 'error' | 'debug';

export interface LogPayload {
  level: LogLevel;
  message: string;
  meta?: Record<string, unknown>;
  userId?: number;
  url?: string;
  method?: string;
  environment?: string;
}

/**
 * Priprema meta objekt za logging - serijalizira sve objekte sigurno.
 */
export function serializeMeta(
  meta: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!meta) return undefined;

  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(meta)) {
    if (val === undefined) continue;
    result[key] = typeof val === 'object' && val !== null ? safeStringify(val) : val;
  }
  return result;
}

/**
 * Ekstrahira čisti stack trace, filtrirajući irelevantne linije.
 * Source maps su već aplicirane na error.stack ako je source-map-support instaliran.
 */
function getCleanStackTrace(error: Error, maxLines = 20): string {
  const stack = error.stack ?? '';
  const lines = stack.split('\n');

  // Zadrži prvu liniju (error message) i filtriraj ostatak
  const [messageLine, ...stackLines] = lines;

  const relevantLines = stackLines
    .filter((line) => {
      // Filtriraj node_modules osim ako nije @prisma ili src (korisno za debugging)
      if (line.includes('node_modules') && !line.includes('@prisma')) {
        return false;
      }
      // Filtriraj interne Node.js pozive
      if (line.includes('node:internal')) {
        return false;
      }
      // Filtriraj Next.js internal routing (osim server dijela)
      if (line.includes('next/dist') && !line.includes('next/dist/server')) {
        return false;
      }
      return true;
    })
    .slice(0, maxLines);

  return [messageLine, ...relevantLines].join('\n');
}

/**
 * Priprema kompletan log payload iz error-a i dodatnih informacija.
 */
export function prepareLogPayload(
  error: unknown,
  options: {
    level?: LogLevel;
    meta?: Record<string, unknown>;
    userId?: number;
    url?: string;
    method?: string;
    environment?: string;
  } = {}
): LogPayload {
  const {
    level = 'error',
    meta = {},
    userId,
    url,
    method,
    environment = process.env.ENVIRONMENT || process.env.NODE_ENV,
  } = options;

  const message = formatErrorForLog(error);

  // Pripremi meta sa dodatnim info o erroru
  const enrichedMeta: Record<string, unknown> = { ...meta };

  // Dodaj stack ako je Error - koristi čisti stack trace
  // Source maps su automatski aplicirane preko source-map-support paketa
  if (error instanceof Error && error.stack) {
    enrichedMeta.stack = getCleanStackTrace(error);
  }

  // Dodaj originalni error kao JSON (za debugging)
  if (error && typeof error === 'object') {
    enrichedMeta.originalError = safeStringify(error);
  }

  return {
    level,
    message,
    meta: serializeMeta(enrichedMeta),
    userId,
    url,
    method,
    environment,
  };
}
